// A JOB, WITH EVERY CHARGE IT HAS DRAWN UNDER IT — nothing folded, nothing elsewhere.
//
// Two kinds of line: a recorded charge (its state, what is left, its invoice photo and its
// actions), and a cost waiting to be charged on. A job that does not bill yet holds only
// the second, and says so — that is the job whose charge used to be drawn loose above
// the retainer's jobs, where nobody looking under the job would find it.
//
// A job's figures are its own. Nothing here is added to the retainer it sits in.
import he from '../../i18n/he';
import Money from '../../components/Money';
import DateText from '../../components/DateText';
import Button from '../../components/Button';
import ChargeState from '../../components/ChargeState';
import { useLookups } from '../../context/LookupsContext';
import ChargeAmounts from './ChargeAmounts';
import ChargeFiles from './ChargeFiles';
import { rowAsCard, chargeLabel } from './boardRows';
import RowActions from './RowActions';
import { PartlyPaid } from './ActionItem';

const t = he.collections.board;
const p = he.moneyScreen.item;

// Exported: a one-off project's own invoices are drawn as these same lines (ClientView).
export function ChargeLine({ card, row, acts }) {
  const tone = row.collectible ? (row.overdue ? 'late' : 'owed') : 'paid';
  return (
    <div className={`mp-line mp-tone-${tone}`}>
      <span className="mp-line-main">
        <ChargeState row={row} />
        <span>{chargeLabel(row)} · <DateText value={row.issueDate} /></span>
        <span className="muted">{t.amount} <Money value={row.amount} /></span>
        <PartlyPaid row={row} />
        {row.collectible && (
          <span className="muted">{row.overdue ? p.lateSince : p.dueBy} <DateText value={row.dueDate} /></span>
        )}
      </span>
      <span className="mp-line-side">
        {row.collectible && <Money className="mp-amount sm" value={row.balance} />}
        <ChargeFiles row={row} onChanged={acts.onFilesChanged} />
        <RowActions kind="recorded" card={card} row={row} acts={acts} />
      </span>
    </div>
  );
}

function RaiseLine({ card, row, acts }) {
  const lookups = useLookups();
  return (
    <div className="mp-line mp-tone-todo">
      <span className="mp-line-main">
        <span className="badge amber">{t.toBill}</span>
        <strong>{row.description || lookups.categoryLabel(row.category)}</strong>
        <ChargeAmounts row={row} />
      </span>
      <span className="mp-line-side">
        <RowActions kind="charge" card={card} row={row} acts={acts} />
      </span>
    </div>
  );
}

export default function JobBlock({ node, acts }) {
  const { card } = node;
  let kindLabel = p.notBilling;
  if (card) kindLabel = card.oneOff ? t.kindOneOff : t.kindBrokerage;
  const empty = node.charges.length === 0 && node.toRaise.length === 0;
  return (
    <div className="mp-job">
      <div className="mp-job-head">
        <span className="mp-job-title">{node.title}</span>
        <span className="badge gray">{kindLabel}</span>
        {card && (
          <span className="mp-figures muted">
            {card.agreedAmount != null && <span>{t.agreed} <Money value={card.agreedAmount} /></span>}
            <span>{t.invoiced} <Money value={card.invoiced} /></span>
            <span>{t.collectedTotal} <Money value={card.collected} /></span>
          </span>
        )}
      </div>
      {node.toRaise.map((e) => (
        <RaiseLine key={`e${e.expenseId}`} card={card || rowAsCard(e)} row={e} acts={acts} />
      ))}
      {node.charges.map((c) => <ChargeLine key={`i${c.invoiceId}`} card={card} row={c} acts={acts} />)}
      {empty && <p className="mp-hint">{he.moneyScreen.tree.noCharges}</p>}
      {card && (
        <Button small variant="ghost" onClick={() => acts.onRecordJob(card)}>{t.addCharge}</Button>
      )}
    </div>
  );
}
